import { useEffect, useState } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { ArrowLeft } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'
import { ShareButton } from '@/widgets/share-button'

export interface ProductFloatingHeaderProps {
  /**
   * Название товара
   */
  productName: string
  /**
   * ID товара
   */
  productId: string
  /**
   * Slug товара для ссылки
   */
  productSlug: string
  /**
   * Тип товара
   */
  productType: 'tea' | 'teaware'
  /**
   * URL изображения
   */
  imageUrl?: string
  /**
   * Описание товара
   */
  productDescription?: string
  /**
   * Цена товара
   */
  productPrice: number
  /**
   * В наличии
   */
  isInStock?: boolean
  /**
   * Показывать ли header (для анимации)
   */
  show?: boolean
}

/**
 * Виджет: Floating Header для страницы товара
 * 
 * Минималистичный header, который появляется при скролле.
 * Прозрачный поверх галереи, становится opaque при скролле вниз.
 */
export function ProductFloatingHeader({
  productName,
  productId,
  productSlug,
  productType,
  imageUrl,
  productDescription,
  productPrice,
  isInStock = true,
  show = true,
}: ProductFloatingHeaderProps) {
  const { scrollY } = useScroll()
  const [isScrolled, setIsScrolled] = useState(false)
  const router = useRouter()
  const { hapticFeedback } = useTelegram()
  
  // Определяем, прокручен ли экран
  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      setIsScrolled(latest > 100)
    })
    return unsubscribe
  }, [scrollY])
  
  // Анимация прозрачности и фона
  const headerOpacity = useTransform(scrollY, [0, 100], [show ? 1 : 0, 1])
  const headerBgOpacity = useTransform(scrollY, [0, 100], [0, 1])
  const titleOpacity = useTransform(scrollY, [0, 100], [0, 1])
  
  if (!show && !isScrolled) {
    return null
  }
  
  return (
    <motion.div
      style={{
        opacity: headerOpacity,
        paddingTop: 'calc(var(--top-inset, 0px) + 12px)',
      }}
      className={cn(
        'fixed top-0 left-0 right-0 z-30',
        'lg:left-[240px]',
        'flex items-center justify-between px-4 lg:px-6 py-3',
        'transition-colors duration-150'
      )}
    >
      {/* Фон с blur */}
      <motion.div
        style={{
          opacity: headerBgOpacity,
        }}
        className="absolute inset-0 bg-paper/95 dark:bg-[#0F1113]/95 backdrop-blur-md"
        aria-hidden="true"
      />
      
      {/* Контент */}
      <div className="relative flex items-center justify-between w-full">
        {/* Кнопка назад */}
        <button
          type="button"
          aria-label="Назад"
          onClick={() => { hapticFeedback.impactOccurred('light'); router.history.back(); }}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-full',
            'bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm',
            'border border-silk/50 dark:border-dark-border/50',
            'text-gray-600 dark:text-gray-400',
            'active:scale-95 transition-all shadow-sm',
            !isScrolled && 'bg-black/20 dark:bg-white/10 border-transparent text-white',
          )}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        {/* Название товара (показывается при скролле) */}
        <motion.div
          style={{
            opacity: titleOpacity,
          }}
          className="flex-1 px-4"
        >
          <h1 className="text-base font-semibold text-ink dark:text-white truncate text-center">
            {productName}
          </h1>
        </motion.div>
        
        {/* Кнопка поделиться */}
        <ShareButton
          productName={productName}
          productId={productId}
          productSlug={productSlug}
          productType={productType}
          imageUrl={imageUrl}
          productDescription={productDescription}
          productPrice={productPrice}
          isInStock={isInStock}
          size="md"
          className={cn(
            !isScrolled && 'bg-black/20 dark:bg-white/10 border-transparent text-white'
          )}
        />
      </div>
    </motion.div>
  )
}


